// Render-only stroke geometry. Reads raw points, never writes them.
import { STRIDE, type Affine, type Tool } from '../../model/types';

const P_REF = 0.5;
const curve = (p: number) => 0.3 + 1.4 * Math.pow(Math.min(Math.max(p, 0), 1), 0.9);
const CURVE_REF = curve(P_REF);

/** Rendered width for a sample. Pen: baseWidth at pressure 0.5. Highlighter: flat. */
export function sampleWidth(tool: Tool, baseWidth: number, pressure: number): number {
  if (tool === 'highlighter') return baseWidth;
  return Math.max(baseWidth * 0.2, (baseWidth * curve(pressure)) / CURVE_REF);
}

export interface OutlineOpts {
  tool: Tool;
  baseWidth: number;
  transform?: Affine | undefined;
}

const CAP_STEPS = 8;

/**
 * Closed outline polygon (flat [x0,y0,x1,y1,…]) in page units for a stride-6
 * point buffer. `extra` (predicted samples) are appended for preview only.
 */
export function buildOutline(points: Float32Array, opts: OutlineOpts, extra?: ArrayLike<number>): number[] {
  // 1. Collect render points: transform, drop near-duplicates, light smoothing.
  const xs: number[] = [], ys: number[] = [], rs: number[] = [];
  const t = opts.transform;
  const minDist = Math.max(0.05, opts.baseWidth * 0.08);
  const push = (x0: number, y0: number, p: number) => {
    const x = t ? t[0] * x0 + t[2] * y0 + t[4] : x0;
    const y = t ? t[1] * x0 + t[3] * y0 + t[5] : y0;
    const r = sampleWidth(opts.tool, opts.baseWidth, p) / 2;
    const n = xs.length;
    if (n > 0 && Math.hypot(x - xs[n - 1]!, y - ys[n - 1]!) < minDist) {
      rs[n - 1] = Math.max(rs[n - 1]!, r);
      return;
    }
    xs.push(x); ys.push(y); rs.push(r);
  };
  for (let i = 0; i < points.length; i += STRIDE) push(points[i]!, points[i + 1]!, points[i + 2]!);
  if (extra) for (let i = 0; i < extra.length; i += STRIDE) push(extra[i]!, extra[i + 1]!, extra[i + 2]!);

  const n = xs.length;
  if (n === 0) return [];
  if (n === 1) return circle(xs[0]!, ys[0]!, rs[0]!);

  // Pen-up samples often carry pressure 0; don't let the tip collapse to a needle.
  if (opts.tool === 'pen') {
    for (let i = 1; i < n; i++) rs[i] = Math.max(rs[i]!, rs[i - 1]! * 0.75);
    for (let i = n - 2; i >= 0; i--) rs[i] = Math.max(rs[i]!, rs[i + 1]! * 0.75);
  }
  const sx = smooth(xs), sy = smooth(ys);

  // 2. Offset curves.
  const left: number[] = [], right: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1), b = Math.min(n - 1, i + 1);
    let dx = sx[b]! - sx[a]!, dy = sy[b]! - sy[a]!;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;
    const r = rs[i]!;
    left.push(sx[i]! - dy * r, sy[i]! + dx * r);
    right.push(sx[i]! + dy * r, sy[i]! - dx * r);
  }

  // 3. left forward, end cap, right backward, start cap.
  const out = left.slice();
  arc(out, sx[n - 1]!, sy[n - 1]!, rs[n - 1]!, angle(sx, sy, n - 2, n - 1) + Math.PI / 2, -Math.PI);
  for (let i = n - 1; i >= 0; i--) out.push(right[i * 2]!, right[i * 2 + 1]!);
  arc(out, sx[0]!, sy[0]!, rs[0]!, angle(sx, sy, 0, 1) - Math.PI / 2, -Math.PI);
  return out;
}

function smooth(v: number[]): number[] {
  const n = v.length;
  if (n < 3) return v.slice();
  const out = new Array<number>(n);
  out[0] = v[0]!;
  out[n - 1] = v[n - 1]!;
  for (let i = 1; i < n - 1; i++) out[i] = 0.25 * v[i - 1]! + 0.5 * v[i]! + 0.25 * v[i + 1]!;
  return out;
}

const angle = (x: number[], y: number[], i: number, j: number) => Math.atan2(y[j]! - y[i]!, x[j]! - x[i]!);

/** Appends arc points from `start` sweeping `sweep` radians (exclusive of endpoints). */
function arc(out: number[], cx: number, cy: number, r: number, start: number, sweep: number) {
  for (let k = 1; k < CAP_STEPS; k++) {
    const a = start + (sweep * k) / CAP_STEPS;
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
}

function circle(cx: number, cy: number, r: number): number[] {
  const out: number[] = [];
  for (let k = 0; k < CAP_STEPS * 2; k++) {
    const a = (Math.PI * k) / CAP_STEPS;
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return out;
}

export interface Rect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function polygonBounds(poly: number[]): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    const x = poly[i]!, y = poly[i + 1]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/** Traces a polygon into a canvas path (Path2D or a context). */
export function tracePolygon(path: { moveTo(x: number, y: number): void; lineTo(x: number, y: number): void; closePath(): void }, poly: number[]): void {
  if (poly.length < 2) return;
  path.moveTo(poly[0]!, poly[1]!);
  for (let i = 2; i < poly.length; i += 2) path.lineTo(poly[i]!, poly[i + 1]!);
  path.closePath();
}
